import { parseCustomerSegments, type CustomerSegment, type ShopperSegment } from './product-eligibility';

export const SEGMENT_COOKIE_NAME = 'b2b_segment';

export interface SegmentCookiePayload {
    siteId: string;
    customerId: string;
    segmentationActive: boolean;
    isAdminAccess: boolean;
    segments: CustomerSegment[];
}

const cookieRegExp = new RegExp(`(?:^|;\\s*)${SEGMENT_COOKIE_NAME}=([^;]+)`);

/** Return the payload if it has the expected shape; otherwise null. */
export function validateSegmentCookiePayload(value: unknown): SegmentCookiePayload | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const candidate = value as Partial<SegmentCookiePayload>;
    if (
        typeof candidate.siteId !== 'string' ||
        candidate.siteId.length === 0 ||
        typeof candidate.customerId !== 'string' ||
        candidate.customerId.length === 0 ||
        typeof candidate.segmentationActive !== 'boolean' ||
        typeof candidate.isAdminAccess !== 'boolean'
    ) {
        return null;
    }

    const segments = parseCustomerSegments(candidate.segments);
    if (!segments) {
        return null;
    }

    return {
        siteId: candidate.siteId,
        customerId: candidate.customerId,
        segmentationActive: candidate.segmentationActive,
        isAdminAccess: candidate.isAdminAccess,
        segments,
    };
}

/** True when the cookie belongs to this site and customer. */
export function isSegmentCookieForShopper(
    payload: SegmentCookiePayload,
    opts: { siteId: string; customerId: string }
): boolean {
    return payload.siteId === opts.siteId && payload.customerId === opts.customerId;
}

/** Map a valid cookie payload to ShopperSegment (`available: true`). */
export function toShopperSegment(payload: SegmentCookiePayload): ShopperSegment {
    return {
        available: true,
        segmentationActive: payload.segmentationActive,
        isAdminAccess: payload.isAdminAccess,
        segments: [...payload.segments],
    };
}

/** Decodes the format written by React Router's `createCookie` for an ASCII payload. */
export function parseSegmentCookie(cookieHeader: string): SegmentCookiePayload | null {
    const match = cookieHeader.match(cookieRegExp);
    if (!match) {
        return null;
    }

    try {
        return validateSegmentCookiePayload(JSON.parse(atob(decodeURIComponent(match[1]))));
    } catch {
        return null;
    }
}
